import {useEffect, useState} from 'react';
import {
    CANVAS_W, CANVAS_H,
    TOOLBAR_W, RIGHT_PANEL_W, CHAT_SIDEBAR_W, GAP_W,
} from './layout';

// Tracks viewport size + orientation and derives all layout flags
// (mobile / landscape / spectator slots) and the canvas scale factor.
//
// Returning a flat object keeps it ergonomic for the (large) Drawing
// component and makes the layout rules easy to read in one place.
// Use window.innerHeight (not visualViewport) so the on-screen keyboard
// does not shrink the canvas — the chat input snaps the page back to the
// top instead (see ChatBox handleChatInputFocus).
function getViewportHeight() {
    if (typeof window === 'undefined') return 0;
    return window.innerHeight;
}

export default function useResponsiveLayout({isSpectator, isGuessMode}) {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [windowHeight, setWindowHeight] = useState(getViewportHeight());
    const [isLandscape, setIsLandscape] = useState(
        () => window.innerWidth > window.innerHeight
    );

    useEffect(() => {
        const update = () => {
            setWindowWidth(window.innerWidth);
            setWindowHeight(getViewportHeight());
            setIsLandscape(window.innerWidth > window.innerHeight);
        };
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('resize', update);
        };
    }, []);

    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isTV = /Tizen|SmartTV|SMART-TV|Web0S|webOS|NetCast|BRAVIA|Viera|Roku/i.test(navigator.userAgent || '');
    const isMobile = windowWidth <= 768 || (isTouchDevice && windowWidth <= 1024);
    const isMobileLandscape = (isMobile || isTV || (isLandscape && windowHeight <= 500) || isSpectator) && isLandscape;
    const isMobilePortrait = isMobile && !isMobileLandscape;

    const showRightPanel = !isMobile && !isSpectator && !isMobileLandscape;
    const showChatSidebar = isMobileLandscape; // chat+players as right sidebar in landscape
    // Left toolbar is hidden on portrait mobile (moved below the canvas).
    const showLeftTools = (!isMobile && !isSpectator) || (isMobileLandscape && !isSpectator);
    const showInlinePlayers = (isMobile || isSpectator) && !isMobileLandscape;

    const effectiveNonCanvasW = (showLeftTools ? TOOLBAR_W : 0)
        + (showRightPanel ? GAP_W + RIGHT_PANEL_W : 0)
        + (showChatSidebar ? GAP_W + CHAT_SIDEBAR_W : 0);

    // Spectator: fit canvas in viewport accounting for chrome above and chat below.
    const SPECTATOR_CHAT_H = 160;
    const SPECTATOR_CHROME_H = 24 + (isGuessMode ? 44 : 0) + 4 + SPECTATOR_CHAT_H;
    // Mobile landscape: fit canvas vertically. In guess mode the header is merged into the status bar.
    const LANDSCAPE_CHROME_H = (isGuessMode ? 46 : 22) + 8;
    // Portrait mobile: header + bar + tools + chat + input + safety.
    const PORTRAIT_CHROME_H = (isGuessMode ? 38 : 24) + 44 + 160 + 36 + 8;

    const scaleW = Math.max(0.1, (windowWidth - effectiveNonCanvasW) / CANVAS_W);
    const scaleH = isSpectator && !isMobile && !isMobileLandscape
        ? Math.max(0.1, (windowHeight - SPECTATOR_CHROME_H) / CANVAS_H)
        : isMobileLandscape
            ? Math.max(0.1, (windowHeight - LANDSCAPE_CHROME_H) / CANVAS_H)
            : isMobile
                ? Math.max(0.1, (windowHeight - PORTRAIT_CHROME_H) / CANVAS_H)
                : Infinity;
    const maxScale = isSpectator ? Infinity : 1;
    const scale = Math.min(maxScale, scaleW, scaleH);

    return {
        windowWidth, windowHeight, isLandscape,
        isTouchDevice, isTV,
        isMobile, isMobileLandscape, isMobilePortrait,
        showRightPanel, showChatSidebar, showLeftTools, showInlinePlayers,
        scale,
        SPECTATOR_CHAT_H,
    };
}
